import { useEventListener } from '@vueuse/core';

export const ACTIVE_ORG_STORAGE_KEY = 'rl:activeOrgId';

export type SyncTrigger = 'storage' | 'visibilitychange' | 'api-401' | 'api-403' | 'seed';
type SessionChange = { kind: 'orgChanged'; trigger: SyncTrigger };

// Module-level so the fetchClient backstop, layout composable, and reload watcher
// share one instance. `baseline` is undefined until seeded from session state,
// then the active org UUID (or null once logged out).
export const sessionChange = ref<SessionChange | null>(null);
let baseline: string | null | undefined;

// A missing/cleared key or the literal 'null' both mean "logged out".
function readStored(): string | null {
  try {
    const raw = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
    return raw === null || raw === 'null' ? null : raw;
  } catch {
    return baseline ?? null; // localStorage unavailable -> assume no change
  }
}

// Persist the active org (or null on logout) and update the in-memory baseline.
export function markActiveOrg(orgId: string | null) {
  baseline = orgId;
  try {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId ?? 'null');
  } catch {
    // localStorage unavailable; the 401/403 backstop still catches mismatches.
  }
}

// Flags a cross-tab change only when another tab switched to a DIFFERENT org.
// Logout (null) is intentionally ignored — the stale tab's next request 401s and
// redirects to login via the existing flow.
export function checkForMismatch(trigger: SyncTrigger) {
  // Bail until the baseline is seeded, or if a switch is already queued.
  if (baseline === undefined || sessionChange.value !== null) return;

  const current = readStored();
  // A real switch means another tab moved to a DIFFERENT org. A null (logout) is
  // ignored — the stale tab's next request 401s into the existing login flow.
  const switchedToDifferentOrg = current !== null && current !== baseline;
  if (!switchedToDifferentOrg) return;

  // Analytics is emitted by SessionChangedModal when it opens (keeps this module free of
  // the analytics import, so fetchClient can import it without a cycle).
  sessionChange.value = { kind: 'orgChanged', trigger };
}

// Adopt the org another tab persisted as our new baseline. Called after a
// cross-tab change is resolved (soft reload) so the same mismatch isn't
// re-detected on the next trigger.
export function resyncBaseline() {
  baseline = readStored();
}

// Mounted once in the authenticated app shell. Seeds the baseline from the
// rendered session state (NOT localStorage) once an active org exists, then
// wires the storage + visibilitychange triggers.
export function useActiveOrgSync(currentOrgId: MaybeRefOrGetter<string | undefined>) {
  watch(
    () => toValue(currentOrgId),
    (id) => {
      if (!id || baseline !== undefined) return;

      // First tab this session: establish the shared key.
      if (readStored() === null) {
        markActiveOrg(id);
        return;
      }

      // Another tab already wrote an org. Adopt our session's org as the
      // in-memory baseline but DON'T overwrite localStorage — if we re-opened
      // stale against a newer org, surface it via checkForMismatch rather than
      // clobbering their value.
      baseline = id;
      checkForMismatch('seed');
    },
    { immediate: true },
  );

  useEventListener(window, 'storage', (e: StorageEvent) => {
    if (e.key === ACTIVE_ORG_STORAGE_KEY) checkForMismatch('storage');
  });
  useEventListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible') checkForMismatch('visibilitychange');
  });

  return { sessionChange };
}
